<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useAuthStore } from '@/stores/auth';
  import { useMenuStore } from '@/stores/menu';
  import { useRouter } from 'vue-router';
  import AppMenuItem from './AppMenuItem.vue';
  
  const userStore = useAuthStore();
  const menuStore = useMenuStore();
  const router = useRouter();

  // 메뉴 로딩 상태 추가
  const isMenuReady = ref(false);

  // 권한 체크 함수
  const checkAuthorization = (item) => {
    if (!item.to) return true; // to 속성이 없으면 기본적으로 허용
    
    // 라우터 정보를 가져와서 권한 체크
    try {
      const route = router.resolve(item.to);
      if (!route.meta || !route.meta.permission) return true;
      return userStore.hasAnyPermission(route.meta.permission);
    } catch (error) {
      console.warn(`Invalid route: ${item.to}`);
      return true; // 유효하지 않은 라우트는 기본적으로 허용
    }
  };

  // 메뉴 아이템에 권한 정보를 추가하되 기존 객체를 재사용
  const processMenuItems = (items) => {
    if (!items) return [];
    
    return items.filter(item => {
      // 현재 아이템의 권한 체크
      item.authorized = checkAuthorization(item);
      
      // 하위 메뉴가 있는 경우 재귀적으로 처리
      if (item.items) {
        item.items = processMenuItems(item.items);
        // 하위 메뉴 중 하나라도 권한이 있으면 상위 메뉴 표시
        item.authorized = item.items.length > 0;
      }
      
      return item.authorized;
    });
  };

  // 권한이 적용된 메뉴 아이템
  const authorizedMenuItems = computed(() => {
    if (!userStore.isAuthenticated || !userStore.user) return [];
    return processMenuItems(menuStore.menuItems);
  });

  // 사용자 정보 변경 감지
  watch(
    () => userStore.user,
    async (newUser) => {
      if (newUser) {
        isMenuReady.value = true;
      }
    }
  );

  // 컴포넌트 마운트 시 사용자 정보 확인
  onMounted(async () => {
    if (userStore.isAuthenticated && !userStore.user) {
      try {
        await userStore.fetchUserInfo();
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    }
    isMenuReady.value = true;
  });
</script>

<template>
  <ul v-if="isMenuReady" class="layout-menu">
    <template v-for="(item, i) in authorizedMenuItems">
      <app-menu-item
        v-if="!item.separator"
        :key="item"
        :item="item"
        :index="i"
      ></app-menu-item>
      <li
        v-if="item.separator"
        :key="item"
        class="menu-separator"
      ></li>
    </template>
  </ul>
  <div v-else class="layout-menu-loading">
    <i class="pi pi-spin pi-spinner"></i>
  </div>
</template>

<style lang="scss" scoped>
.layout-menu-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  i {
    font-size: 2rem;
  }
}
</style>
